import { useCallback } from 'react';
import * as amplitude from '@amplitude/analytics-browser';
import { useStoreInfoStore } from '../../stores/useStoreInfoStore';

interface EventProperties {
  [key: string]: unknown;
}

interface SessionProperties {
  sessionId: string;
  storeId: string;
  storeUrl: string;
  storeName: string;
}

export const useAnalytics = () => {
  const sessionSource = useStoreInfoStore((s) => s.sessionSource);
  const amplitudeEnabled = useStoreInfoStore(
    (s) => s.store?.config?.config?.amplitudeEnabled
  );

  const isAmplitudeEnabled =
    sessionSource === 'playground' || amplitudeEnabled === true;

  const trackEvent = useCallback(
    (eventName: string, properties?: EventProperties) => {
      if (!isAmplitudeEnabled) return;
      try {
        amplitude.track(eventName, properties);
      } catch (error) {
        console.error('Failed to track event in Amplitude:', error);
      }
    },
    [isAmplitudeEnabled]
  );

  const identifySession = useCallback(
    (sessionId: string, sessionProperties?: SessionProperties) => {
      if (!isAmplitudeEnabled) return;
      try {
        const deviceId = amplitude.getDeviceId();
        const userId = sessionId;

        amplitude.setUserId(userId);

        if (sessionProperties) {
          const identify = new amplitude.Identify();

          Object.entries(sessionProperties).forEach(([key, value]) => {
            identify.set(key, value);
          });
          amplitude.identify(identify);
        }
      } catch (error) {
        console.error('Failed to identify session in Amplitude:', error);
      }
    },
    [isAmplitudeEnabled]
  );

  const setUserProperties = useCallback((properties: EventProperties) => {
    try {
      const identify = new amplitude.Identify();
      Object.entries(properties).forEach(([key, value]) => {
        identify.set(key, value as string);
      });
      amplitude.identify(identify);
    } catch (error) {
      console.error('Failed to set user properties in Amplitude:', error);
    }
  }, []);

  return { trackEvent, identifySession, setUserProperties };
};
